import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useNavigate, useLocation } from "react-router-dom";
import { LucideIcon, ExternalLink } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

interface NavItem {
  title: string;
  path: string;
  icon: LucideIcon;
  description?: string;
}

interface Section {
  title: string;
  items: NavItem[];
}

interface BottomNavSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  items: NavItem[];
  sections?: Section[];
}

const BottomNavSheet = ({ isOpen, onClose, title, items, sections }: BottomNavSheetProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigation = (path: string) => {
    if (path.startsWith('http')) {
      window.open(path, '_blank');
    } else {
      navigate(path);
    }
    onClose();
  };

  const isExternal = (path: string) => path.startsWith('http');
  const isActive = (path: string) => !isExternal(path) && location.pathname === path;

  const renderItem = (item: NavItem) => (
    <button
      key={item.path}
      onClick={() => handleNavigation(item.path)}
      className={`flex items-center gap-3 p-3 rounded-xl transition-colors text-left group w-full ${
        isActive(item.path) ? 'bg-primary/10' : 'hover:bg-muted'
      }`}
    >
      <div className={`flex items-center justify-center w-10 h-10 rounded-xl transition-colors flex-shrink-0 ${
        isActive(item.path) 
          ? 'bg-primary text-primary-foreground' 
          : 'bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground'
      }`}>
        <item.icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm flex items-center gap-1 ${isActive(item.path) ? 'font-semibold text-primary' : 'font-medium text-foreground'}`}>
          {item.title}
          {isExternal(item.path) && <ExternalLink className="w-3 h-3 text-muted-foreground" />}
        </p>
        {item.description && (
          <p className="text-xs text-muted-foreground">{item.description}</p>
        )}
      </div>
    </button>
  );

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent 
        side="bottom" 
        className="rounded-t-3xl h-[70vh] flex flex-col p-0"
      >
        <SheetHeader className="px-6 pt-6 pb-2 flex-shrink-0">
          <div className="w-12 h-1 bg-muted-foreground/30 rounded-full mx-auto mb-3" />
          <SheetTitle className="text-lg font-bold text-primary">{title}</SheetTitle>
        </SheetHeader>
        
        <ScrollArea className="flex-1 px-6">
          {sections ? (
            <div className="pb-8 space-y-4">
              {sections.map((section) => (
                <div key={section.title}>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 px-1">
                    {section.title}
                  </h3>
                  <div className="grid gap-1">
                    {section.items.map(renderItem)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid gap-1 pb-8">
              {items.map(renderItem)}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default BottomNavSheet;
